// Phase 0b PR 6: TanStack Query bindings for the Award Comparison
// workspace. Three reads / three writes; all route through zou's
// ProjectDataService.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { projectDataClient } from '../../../lib/connect/clients';
import { resolvedToast } from '../_shared/components/review/reviewItemCopy';
import { PROGRAM_CHANGE_SETS_KEY } from '../Programs/v2/modificationHooks';
const CHANGE_SET_KEY = 'change-set-discrepancies';
/**
 * List the discrepancy roster for a change-set. Re-uses the
 * AwardComparisonSummary shape so the workspace component renders the
 * same payload BeginAwardComparison returns.
 */ export function useChangeSetDiscrepancies(changeSetId) {
    return useQuery({
        queryKey: [
            CHANGE_SET_KEY,
            changeSetId
        ],
        enabled: !!changeSetId,
        queryFn: async ()=>{
            const resp = await projectDataClient.listChangeSetDiscrepancies({
                changeSetId
            });
            if (!resp.summary) {
                throw new Error('ListChangeSetDiscrepancies returned no summary');
            }
            return resp.summary;
        }
    });
}
/**
 * ResolveDiscrepancy mutation. Surfaces backend rejections (Cerbos deny,
 * validation) as toasts so the reviewer sees them inline.
 */ export function useResolveDiscrepancy() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.resolveDiscrepancy({
                discrepancyId: args.discrepancyId,
                resolutionState: args.resolutionState,
                decisionRationale: args.decisionRationale,
                decidedValueJson: args.decidedValueJson ?? ''
            });
            return resp.discrepancy;
        },
        onSuccess: (_disc, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_KEY,
                    vars.changeSetId
                ]
            });
            // Program Home's Do Next row counts open review items off the roster.
            queryClient.invalidateQueries({
                queryKey: [
                    PROGRAM_CHANGE_SETS_KEY
                ]
            });
            toast.success(vars.savedToast ?? resolvedToast(vars.resolutionState));
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to resolve discrepancy');
        }
    });
}
/**
 * Bulk "Accept" every supplied discrepancy on a change-set. This is an
 * admin-only convenience (the discrepancy policy already lets `admin` resolve
 * any row regardless of functional owner — see discrepancy_policy.yaml); it
 * just saves clicking each row. There is no bulk RPC, so it fans out the
 * existing per-item ResolveDiscrepancy with a bounded concurrency, then
 * invalidates the roster ONCE (not per call) so the UI doesn't thrash.
 */ export function useApproveAllDiscrepancies() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const ids = [
                ...args.discrepancyIds
            ];
            const failures = [];
            const concurrency = 8;
            for(let i = 0; i < ids.length; i += concurrency){
                const batch = ids.slice(i, i + concurrency);
                const results = await Promise.allSettled(batch.map((discrepancyId)=>projectDataClient.resolveDiscrepancy({
                        discrepancyId,
                        resolutionState: 'Accepted',
                        decisionRationale: args.decisionRationale,
                        decidedValueJson: ''
                    })));
                results.forEach((r, j)=>{
                    if (r.status === 'rejected') failures.push(batch[j]);
                });
            }
            return {
                total: ids.length,
                failed: failures.length
            };
        },
        onSuccess: (res, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_KEY,
                    vars.changeSetId
                ]
            });
            if (res.failed > 0) {
                toast.warning(`Approved ${res.total - res.failed} of ${res.total}; ${res.failed} failed`);
            } else {
                toast.success(`Approved all ${res.total} change items`);
            }
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to approve change items');
        }
    });
}
/**
 * Resolves a grouped review entry's members in one action
 * (ReviewItemGroupDrawer): each member keeps its own decision and value, fanned
 * out over the per-item ResolveDiscrepancy with bounded concurrency like
 * Approve all, then one roster invalidation.
 */ export function useResolveDiscrepancyGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const failures = [];
            const concurrency = 8;
            for(let i = 0; i < args.decisions.length; i += concurrency){
                const batch = args.decisions.slice(i, i + concurrency);
                const results = await Promise.allSettled(batch.map((d)=>projectDataClient.resolveDiscrepancy({
                        discrepancyId: d.discrepancyId,
                        resolutionState: d.resolutionState,
                        decisionRationale: d.decisionRationale,
                        decidedValueJson: d.decidedValueJson ?? ''
                    })));
                results.forEach((r, j)=>{
                    if (r.status === 'rejected') failures.push(batch[j].discrepancyId);
                });
            }
            return {
                total: args.decisions.length,
                failed: failures.length
            };
        },
        onSuccess: (res, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_KEY,
                    vars.changeSetId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    PROGRAM_CHANGE_SETS_KEY
                ]
            });
            if (res.failed > 0) {
                toast.warning(`${vars.summary}: ${res.failed} of ${res.total} failed`);
            } else {
                toast.success(vars.summary);
            }
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save the group');
        }
    });
}
/**
 * RouteDiscrepancy mutation — re-assigns to a different functional
 * area. Doesn't change resolution state.
 */ export function useRouteDiscrepancy() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.routeDiscrepancy({
                discrepancyId: args.discrepancyId,
                functionalOwner: args.functionalOwner
            });
            return resp.discrepancy;
        },
        onSuccess: (_disc, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_KEY,
                    vars.changeSetId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to route discrepancy');
        }
    });
}
/**
 * SetDiscrepancyTargetPeriod stamps a reviewer-picked target onto an identity
 * change item whose extraction left it empty (SAD-395): a period, a contract
 * line or an optional task, so the item writes through at commit.
 */ export function useSetDiscrepancyTargetPeriod() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.setDiscrepancyTargetPeriod({
                discrepancyId: args.discrepancyId,
                periodId: args.periodId ?? '',
                targetObjectId: args.targetObjectId ?? ''
            });
            return resp.discrepancy;
        },
        onSuccess: (_disc, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_KEY,
                    vars.changeSetId
                ]
            });
            toast.success('Target set');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to set the target');
        }
    });
}
/**
 * StageContractLineItem (SAD-768): append a reviewer-authored "Add a missing
 * line" or "Correct this line" item onto an open intake or modification set.
 * Money is not written until the set is applied.
 */ export function useStageContractLineItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.stageContractLineItem({
                changeSetId: args.changeSetId,
                action: args.action,
                clinNumber: args.clinNumber,
                title: args.title,
                periodId: args.periodId ?? '',
                optionalTaskNumber: args.optionalTaskNumber ?? 0,
                contractType: args.contractType ?? '',
                price: args.price ?? '',
                fundedAmount: args.fundedAmount ?? '',
                reason: args.reason
            });
            return resp;
        },
        onSuccess: (resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_KEY,
                    vars.changeSetId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    PROGRAM_CHANGE_SETS_KEY
                ]
            });
            if (resp.redirectToCorrect) return;
            toast.success(vars.action === 'add' ? 'Missing line staged — it applies when you commit' : 'Line correction staged — it applies when you commit');
        },
        onError: (err)=>{
            toast.error(apiErrorMessage(err, 'Failed to stage the line'));
        }
    });
}
/**
 * CommitImport mutation — flips the change-set to Applied, creates the
 * Contract Project, writes field lineage, seeds the activation gate.
 * The handler returns either `summary` (success) or `violations`
 * (server-side invariant rejected the commit); callers branch on that.
 */ export function useCommitImport() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.commitImport({
                changeSetId: args.changeSetId,
                contractProjectName: args.contractProjectName ?? ''
            });
            return resp;
        },
        onSuccess: (resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_KEY,
                    vars.changeSetId
                ]
            });
            // The commit creates the contract program server-side — refresh every
            // surface that should now show it, or the programs list / command
            // center stay stale until their 30s staleTime lapses.
            queryClient.invalidateQueries({
                queryKey: [
                    'program'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'command-center'
                ]
            });
            if (resp.summary) {
                toast.success('Contract program created');
            }
        },
        onError: (err)=>{
            // A refused apply is its own sentence (FailedPrecondition, SAD-691):
            // rawMessage, never the "[code]" prefix or a chain.
            toast.error(apiErrorMessage(err, 'Failed to commit import'));
        }
    });
}
